import { useEffect, useState } from "react";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaLock,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import api from "../api";

export default function Checkout() {
  const navigate = useNavigate();

  const [user] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });

  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  const [paymentMethod, setPaymentMethod] = useState("stripe");

  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    shipping_address: "",
    shipping_city: "",
    shipping_state: "",
    shipping_postal_code: "",
    shipping_country: "United States",
  });

  // =========================================================
  // LOAD USER INFORMATION
  // =========================================================

  useEffect(() => {
    if (!user) {
      return;
    }

    setForm((prev) => ({
      ...prev,

      customer_name:
        prev.customer_name ||
        [
          user.first_name,
          user.last_name,
        ]
          .filter(Boolean)
          .join(" ") ||
        user.username ||
        "",

      customer_email:
        prev.customer_email ||
        user.email ||
        "",
    }));
  }, [user]);

  // =========================================================
  // FETCH CART
  // =========================================================

  useEffect(() => {
    const fetchCart = async () => {
      if (!user?.access) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(
          "/api/carts/",
          {
            headers: {
              Authorization:
                `Bearer ${user.access}`,
            },
          }
        );

        const data = Array.isArray(response.data)
          ? response.data
          : [];

        const userId = user.pk || user.id;

        const userCart = data.find(
          (cart) =>
            Number(cart.user) === Number(userId) &&
            cart.is_active === true
        );

        const items = userCart?.items || [];

        setCartItems(
          items.filter(
            (item) =>
              item.product?.is_sold !== true
          )
        );
      } catch (err) {
        console.error(
          "Checkout cart error:",
          err.response?.data || err
        );

        setError(
          "Unable to load your cart."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCart();
  }, [user]);

  // =========================================================
  // FORM CHANGE
  // =========================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =========================================================
  // TOTAL
  // =========================================================

  const cartTotal = cartItems
    .reduce(
      (sum, item) =>
        sum + Number(
          item.total_price || 0
        ),
      0
    )
    .toFixed(2);

  // =========================================================
  // PAYPAL
  // =========================================================

  const startPayPalCheckout = async () => {
    const response = await api.post(
      "/api/create-paypal-order/",
      form,
      {
        headers: {
          Authorization:
            `Bearer ${user.access}`,
        },
      }
    );

    const data = response.data;

    const approveLink =
      data.links?.find(
        (link) =>
          link.rel === "approve"
      );

    if (!approveLink?.href) {
      throw new Error(
        "PayPal approval link was not returned."
      );
    }

    const paypalWindow = window.open(
      approveLink.href,
      "PayPal",
      "width=600,height=700"
    );

    if (!paypalWindow) {
      throw new Error(
        "Your browser blocked the PayPal window. Please allow pop-ups."
      );
    }

    paypalWindow.focus();
  };

  // =========================================================
  // STRIPE
  // =========================================================

  const startStripeCheckout = async () => {
    const response = await api.post(
      "/api/stripe/create-checkout-session/",
      form,
      {
        headers: {
          Authorization:
            `Bearer ${user.access}`,
        },
      }
    );

    const data = response.data;

    if (!data.checkout_url) {
      throw new Error(
        "Stripe checkout URL was not returned."
      );
    }

    window.location.href =
      data.checkout_url;
  };

  // =========================================================
  // SUBMIT PAYMENT
  // =========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user?.access) {
      setError(
        "Please sign in before continuing."
      );
      return;
    }

    if (!cartItems.length) {
      setError(
        "Your cart is empty."
      );
      return;
    }

    if (processing) {
      return;
    }

    setProcessing(true);
    setError("");

    try {
      if (paymentMethod === "stripe") {
        await startStripeCheckout();
      } else {
        await startPayPalCheckout();
      }
    } catch (err) {
      console.error(
        "Payment error:",
        err.response?.data || err
      );

      setError(
        err.response?.data?.detail ||
          err.message ||
          "Unable to start payment. Please try again."
      );

      setProcessing(false);
    }
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <section className="min-h-screen bg-[#F7F3EB] flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-[#D9D0C5] border-t-[#0F766E]" />

          <p className="text-sm text-[#76695D]">
            Loading checkout...
          </p>
        </div>
      </section>
    );
  }

  // =========================================================
  // UI
  // =========================================================

  return (
    <section className="min-h-screen bg-[#F7F3EB] px-4 py-8 sm:px-6 lg:px-8 lg:py-14">

      <div className="mx-auto max-w-7xl">

        {/* BACK */}

        <button
          type="button"
          onClick={() => navigate("/cart")}
          className="
            mb-8
            inline-flex
            items-center
            gap-2
            text-sm
            font-medium
            text-[#6F665D]
            hover:text-[#0F766E]
          "
        >
          <FaArrowLeft className="text-xs" />
          Back to Cart
        </button>

        {/* HEADER */}

        <div className="mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#0F766E]">
            Secure checkout
          </p>

          <h1 className="mt-2 text-4xl font-semibold tracking-tight text-[#332A24] sm:text-5xl">
            Checkout
          </h1>

          <p className="mt-3 text-sm text-[#76695D]">
            Enter your shipping details and choose how you'd like to pay.
          </p>
        </div>

        <form onSubmit={handleSubmit}>

          <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_380px]">

            {/* =================================================
                LEFT
            ================================================= */}

            <div className="space-y-6">

              {/* CONTACT */}

              <div className="rounded-[1.75rem] border border-[#E7DED2] bg-white p-6 shadow-[0_20px_55px_rgba(57,45,35,0.06)] sm:p-8">

                <h2 className="text-xl font-semibold text-[#332A24]">
                  Contact information
                </h2>

                <p className="mt-1 text-sm text-[#8A7D70]">
                  We'll use this information for your order confirmation.
                </p>

                <div className="mt-7 grid gap-5 sm:grid-cols-2">

                  {/* NAME */}

                  <div className="sm:col-span-2">
                    <label className="mb-2 block text-sm font-medium text-[#403831]">
                      Full name
                    </label>

                    <input
                      type="text"
                      name="customer_name"
                      value={form.customer_name}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-[#DED5CA] bg-[#FCFAF7] px-4 py-3.5 text-sm outline-none focus:border-[#0F766E] focus:ring-4 focus:ring-[#0F766E]/10"
                      placeholder="Full name"
                    />
                  </div>

                  {/* EMAIL */}

                  <div>
                    <label className="mb-2 block text-sm font-medium text-[#403831]">
                      Email address
                    </label>

                    <input
                      type="email"
                      name="customer_email"
                      value={form.customer_email}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-[#DED5CA] bg-[#FCFAF7] px-4 py-3.5 text-sm outline-none focus:border-[#0F766E] focus:ring-4 focus:ring-[#0F766E]/10"
                      placeholder="you@example.com"
                    />
                  </div>

                  {/* PHONE */}

                  <div>
                    <label className="mb-2 block text-sm font-medium text-[#403831]">
                      Phone number
                    </label>

                    <input
                      type="tel"
                      name="customer_phone"
                      value={form.customer_phone}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-[#DED5CA] bg-[#FCFAF7] px-4 py-3.5 text-sm outline-none focus:border-[#0F766E] focus:ring-4 focus:ring-[#0F766E]/10"
                      placeholder="+1 425 000 0000"
                    />
                  </div>

                </div>
              </div>

              {/* SHIPPING */}

              <div className="rounded-[1.75rem] border border-[#E7DED2] bg-white p-6 shadow-[0_20px_55px_rgba(57,45,35,0.06)] sm:p-8">

                <h2 className="text-xl font-semibold text-[#332A24]">
                  Shipping address
                </h2>

                <p className="mt-1 text-sm text-[#8A7D70]">
                  Where should we deliver your rug?
                </p>

                <div className="mt-7 space-y-5">

                  {/* ADDRESS */}

                  <div>
                    <label className="mb-2 block text-sm font-medium text-[#403831]">
                      Street address
                    </label>

                    <input
                      type="text"
                      name="shipping_address"
                      value={form.shipping_address}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-[#DED5CA] bg-[#FCFAF7] px-4 py-3.5 text-sm outline-none focus:border-[#0F766E] focus:ring-4 focus:ring-[#0F766E]/10"
                      placeholder="123 Main Street"
                    />
                  </div>

                  {/* CITY / STATE */}

                  <div className="grid gap-5 sm:grid-cols-2">

                    <div>
                      <label className="mb-2 block text-sm font-medium text-[#403831]">
                        City
                      </label>

                      <input
                        type="text"
                        name="shipping_city"
                        value={form.shipping_city}
                        onChange={handleChange}
                        required
                        className="w-full rounded-xl border border-[#DED5CA] bg-[#FCFAF7] px-4 py-3.5 text-sm outline-none focus:border-[#0F766E] focus:ring-4 focus:ring-[#0F766E]/10"
                        placeholder="Seattle"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-[#403831]">
                        State / Province
                      </label>

                      <input
                        type="text"
                        name="shipping_state"
                        value={form.shipping_state}
                        onChange={handleChange}
                        className="w-full rounded-xl border border-[#DED5CA] bg-[#FCFAF7] px-4 py-3.5 text-sm outline-none focus:border-[#0F766E] focus:ring-4 focus:ring-[#0F766E]/10"
                        placeholder="Washington"
                      />
                    </div>

                  </div>

                  {/* ZIP / COUNTRY */}

                  <div className="grid gap-5 sm:grid-cols-2">

                    <div>
                      <label className="mb-2 block text-sm font-medium text-[#403831]">
                        ZIP / Postal code
                      </label>

                      <input
                        type="text"
                        name="shipping_postal_code"
                        value={form.shipping_postal_code}
                        onChange={handleChange}
                        required
                        className="w-full rounded-xl border border-[#DED5CA] bg-[#FCFAF7] px-4 py-3.5 text-sm outline-none focus:border-[#0F766E] focus:ring-4 focus:ring-[#0F766E]/10"
                        placeholder="98101"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-[#403831]">
                        Country
                      </label>

                      <select
                        name="shipping_country"
                        value={form.shipping_country}
                        onChange={handleChange}
                        required
                        className="w-full rounded-xl border border-[#DED5CA] bg-[#FCFAF7] px-4 py-3.5 text-sm outline-none focus:border-[#0F766E] focus:ring-4 focus:ring-[#0F766E]/10"
                      >
                        <option value="United States">
                          United States
                        </option>

                        <option value="Canada">
                          Canada
                        </option>

                        <option value="United Kingdom">
                          United Kingdom
                        </option>

                        <option value="Australia">
                          Australia
                        </option>

                        <option value="Germany">
                          Germany
                        </option>

                        <option value="France">
                          France
                        </option>

                        <option value="Other">
                          Other
                        </option>
                      </select>
                    </div>

                  </div>

                </div>
              </div>

              {/* PAYMENT */}

              <div className="rounded-[1.75rem] border border-[#E7DED2] bg-white p-6 shadow-[0_20px_55px_rgba(57,45,35,0.06)] sm:p-8">

                <h2 className="text-xl font-semibold text-[#332A24]">
                  Payment method
                </h2>

                <p className="mt-1 text-sm text-[#8A7D70]">
                  Choose how you'd like to pay.
                </p>

                <div className="mt-7 grid gap-4 sm:grid-cols-2">

                  {/* STRIPE */}

                  <button
                    type="button"
                    onClick={() =>
                      setPaymentMethod("stripe")
                    }
                    className={`
                      rounded-2xl
                      border-2
                      p-5
                      text-left
                      transition
                      ${
                        paymentMethod === "stripe"
                          ? "border-[#0F766E] bg-[#F2F9F7]"
                          : "border-[#DED5CA] bg-[#FCFAF7]"
                      }
                    `}
                  >

                    <div className="flex items-center justify-between">

                      <span className="text-base font-semibold text-[#332A24]">
                        Card payment
                      </span>

                      <span className="rounded-full bg-[#0F766E] px-3 py-1 text-xs font-semibold text-white">
                        Stripe
                      </span>

                    </div>

                    <p className="mt-2 text-xs leading-5 text-[#76695D]">
                      Pay securely with Visa, Mastercard, Amex,
                      and other supported cards.
                    </p>

                    {paymentMethod === "stripe" && (
                      <div className="mt-4 flex items-center gap-2 text-xs font-medium text-[#0F766E]">
                        <FaCheckCircle />
                        Selected
                      </div>
                    )}

                  </button>

                  {/* PAYPAL */}

                  <button
                    type="button"
                    onClick={() =>
                      setPaymentMethod("paypal")
                    }
                    className={`
                      rounded-2xl
                      border-2
                      p-5
                      text-left
                      transition
                      ${
                        paymentMethod === "paypal"
                          ? "border-[#0F766E] bg-[#F2F9F7]"
                          : "border-[#DED5CA] bg-[#FCFAF7]"
                      }
                    `}
                  >

                    <div className="flex items-center justify-between">

                      <span className="text-base font-semibold text-[#332A24]">
                        PayPal
                      </span>

                      <span className="rounded-full bg-[#F3EEE7] px-3 py-1 text-xs font-semibold text-[#5D5249]">
                        PayPal
                      </span>

                    </div>

                    <p className="mt-2 text-xs leading-5 text-[#76695D]">
                      Continue securely with your PayPal account.
                    </p>

                    {paymentMethod === "paypal" && (
                      <div className="mt-4 flex items-center gap-2 text-xs font-medium text-[#0F766E]">
                        <FaCheckCircle />
                        Selected
                      </div>
                    )}

                  </button>

                </div>

                <div className="mt-5 flex items-start gap-3 rounded-xl bg-[#F8F5F0] p-4">

                  <FaLock className="mt-0.5 shrink-0 text-[#0F766E]" />

                  <p className="text-xs leading-5 text-[#76695D]">
                    Your card information is entered securely on
                    Stripe's payment page. A1 Rugs does not receive
                    your card number.
                  </p>

                </div>

              </div>

              {/* ERROR */}

              {error && (
                <div
                  role="alert"
                  className="
                    rounded-xl
                    border
                    border-red-200
                    bg-red-50
                    px-4
                    py-3
                    text-sm
                    leading-5
                    text-red-700
                  "
                >
                  {error}
                </div>
              )}

            </div>

            {/* =================================================
                RIGHT — SUMMARY
            ================================================= */}

            <aside className="lg:sticky lg:top-24">

              <div className="overflow-hidden rounded-[1.75rem] border border-[#DDD2C4] bg-white shadow-[0_25px_70px_rgba(57,45,35,0.10)]">

                <div className="border-b border-[#EFE8DF] px-6 py-6 sm:px-7">

                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0F766E]">
                    Order summary
                  </p>

                  <h2 className="mt-2 text-2xl font-semibold text-[#332A24]">
                    Your order
                  </h2>

                </div>

                <div className="p-6 sm:p-7">

                  <div className="space-y-4 border-b border-[#EFE8DF] pb-6">

                    {cartItems.map((item) => (

                      <div
                        key={`checkout-${item.id}`}
                        className="flex items-center gap-3"
                      >

                        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-[#F3EEE7]">

                          <img
                            src={item.product_image}
                            alt={item.product_name}
                            className="h-full w-full object-cover"
                          />

                        </div>

                        <div className="min-w-0 flex-1">

                          <p className="truncate text-sm font-medium text-[#403831]">
                            {item.product_name}
                          </p>

                          <p className="mt-1 text-xs text-[#8A7D70]">
                            Qty {item.quantity}
                          </p>

                        </div>

                        <p className="text-sm font-semibold text-[#403831]">
                          $
                          {Number(
                            item.total_price || 0
                          ).toFixed(2)}
                        </p>

                      </div>

                    ))}

                  </div>

                  <div className="space-y-4 border-b border-[#EFE8DF] py-6">

                    <div className="flex justify-between text-sm">

                      <span className="text-[#76695D]">
                        Subtotal
                      </span>

                      <span className="font-medium text-[#403831]">
                        ${cartTotal}
                      </span>

                    </div>

                    <div className="flex justify-between text-sm">

                      <span className="text-[#76695D]">
                        Shipping
                      </span>

                      <span className="font-semibold text-[#0F766E]">
                        Calculated at checkout
                      </span>

                    </div>

                  </div>

                  <div className="flex items-end justify-between py-6">

                    <div>

                      <p className="text-sm text-[#76695D]">
                        Total
                      </p>

                      <p className="mt-1 text-3xl font-bold tracking-tight text-[#332A24]">
                        ${cartTotal}
                      </p>

                    </div>

                    <span className="text-xs text-[#A39487]">
                      USD
                    </span>

                  </div>

                  <button
                    type="submit"
                    disabled={processing}
                    className="
                      w-full
                      rounded-xl
                      bg-[#0F766E]
                      px-5
                      py-4
                      text-sm
                      font-semibold
                      text-white
                      shadow-[0_12px_30px_rgba(15,118,110,0.20)]
                      transition
                      hover:-translate-y-0.5
                      hover:bg-[#0C625C]
                      disabled:cursor-not-allowed
                      disabled:opacity-60
                    "
                  >
                    {processing
                      ? "Starting payment..."
                      : paymentMethod === "stripe"
                      ? "Continue to Card Payment"
                      : "Continue to PayPal"}
                  </button>

                  <div className="mt-4 flex items-center justify-center gap-2 text-xs text-[#8A7D70]">
                    <FaLock className="text-[#0F766E]" />
                    Secure encrypted checkout
                  </div>

                </div>
              </div>

            </aside>

          </div>
        </form>
      </div>
    </section>
  );
}